// Caption feed for /live and /screen: waits while the room is idle, connects
// when it becomes active, reconnects with backoff, de-duplicates on reconnect.

import { CloseCode, type CaptionLine, type RoomState, type RoomToListener } from "../../shared/protocol";
import { Backoff, wsUrl } from "./backoff";

export type FeedState = "waiting" | "connecting" | RoomState;

/** A caption line as shown: translated lines carry their language; `fallback` = English sent instead. */
export type FeedLine = CaptionLine & { lang?: string; fallback?: boolean };

export interface FeedEvents {
  onState(s: FeedState): void;
  /** Lines were replaced wholesale (new session or first connect). */
  onReset(lines: FeedLine[]): void;
  onFinal(line: FeedLine): void;
  onInterim(text: string): void;
  /** Languages the room offers (from its hello). */
  onLangs?(langs: string[]): void;
}

const POLL_MS = 20000;
const HEARTBEAT_MS = 15000;
const DEAD_AFTER_MS = 40000;

export class CaptionFeed {
  private ws: WebSocket | null = null;
  private state: FeedState = "connecting";
  private epoch: string | undefined;
  private lastId: number | undefined;
  private backoff = new Backoff(500, 10000);
  private timer: number | undefined;
  private heartbeat: number | undefined;
  private lastMsgAt = 0;
  private everLive = false;

  /** The session the lines on screen belong to (for feedback after it ends). */
  get currentEpoch(): string | undefined {
    return this.epoch;
  }

  constructor(
    private room: string,
    private ev: FeedEvents,
    private lang = "en",
  ) {}

  /** Switch language: drop this connection and subscribe again, starting from that language's lines. */
  setLang(lang: string): void {
    if (lang === this.lang) return;
    this.lang = lang;
    this.epoch = undefined;
    this.lastId = undefined;
    const ws = this.ws;
    this.ws = null;
    this.stopHeartbeat();
    try {
      ws?.close(1000, "language changed");
    } catch {}
    this.ev.onInterim("");
    this.schedule(0, () => this.check());
  }

  start(): void {
    this.check();
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") this.wake();
    });
    window.addEventListener("online", () => this.wake());
  }

  /** Something suggests the network is back: retry now instead of waiting. */
  private wake(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ping();
      return;
    }
    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) return;
    this.schedule(0, () => this.check());
  }

  private setState(s: FeedState): void {
    if (s === this.state) return;
    this.state = s;
    this.ev.onState(s);
  }

  private schedule(ms: number, fn: () => void): void {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = window.setTimeout(() => {
      this.timer = undefined;
      fn();
    }, ms);
  }

  private async check(): Promise<void> {
    try {
      const r = await fetch(`/api/${this.room}/status`, { cache: "no-store" });
      const s = (await r.json()) as { active: boolean; langs?: string[] };
      if (s.langs) this.ev.onLangs?.(s.langs);
      if (s.active) {
        this.connect();
      } else {
        this.setState("waiting");
        this.ev.onInterim("");
        this.schedule(POLL_MS, () => this.check());
      }
    } catch {
      // Offline or server unreachable.
      if (this.everLive) this.setState("reconnecting");
      this.schedule(this.backoff.next(), () => this.check());
    }
  }

  private connect(): void {
    if (this.ws) return;
    if (!this.everLive) this.setState("connecting");
    const ws = new WebSocket(wsUrl(`/ws/${this.room}/listen`));
    this.ws = ws;
    ws.onopen = () => {
      this.lastMsgAt = Date.now();
      ws.send(JSON.stringify({ type: "subscribe", lang: this.lang, mode: "text", epoch: this.epoch, lastId: this.lastId }));
      this.startHeartbeat();
    };
    ws.onmessage = (e) => {
      this.lastMsgAt = Date.now();
      let m: RoomToListener;
      try {
        m = JSON.parse(e.data);
      } catch {
        return;
      }
      this.handle(m);
    };
    ws.onclose = (e) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.stopHeartbeat();
      this.ev.onInterim("");
      if (e.code === CloseCode.Ended) {
        this.setState("waiting");
        this.backoff.reset();
        this.schedule(POLL_MS, () => this.check());
        return;
      }
      if (this.everLive) this.setState("reconnecting");
      // Room may have ended while we were away – ask before reconnecting.
      this.schedule(this.backoff.next(), () => this.check());
    };
  }

  private handle(m: RoomToListener): void {
    switch (m.type) {
      case "backlog": {
        this.backoff.reset();
        if (m.reset || m.epoch !== this.epoch) {
          this.epoch = m.epoch;
          this.lastId = m.lines.length ? m.lines[m.lines.length - 1].id : undefined;
          this.ev.onReset(m.lines);
        } else {
          for (const l of m.lines) this.acceptFinal(l);
        }
        break;
      }
      case "final":
        this.acceptFinal(m);
        break;
      case "interim":
        this.ev.onInterim(m.text);
        break;
      case "status":
        this.everLive = this.everLive || m.state !== "idle";
        this.setState(m.state === "idle" ? "waiting" : m.state);
        if (m.state !== "live") this.ev.onInterim("");
        break;
      case "hello":
        if (this.epoch && m.epoch !== this.epoch) this.lastId = undefined;
        this.ev.onLangs?.(m.langs);
        break;
    }
  }

  private acceptFinal(l: FeedLine): void {
    if (this.lastId !== undefined && l.id <= this.lastId) return;
    this.lastId = l.id;
    this.ev.onInterim("");
    this.ev.onFinal({ id: l.id, text: l.text, ts: l.ts, lang: l.lang, fallback: l.fallback });
  }

  private ping(): void {
    try {
      this.ws?.send('{"type":"ping"}');
    } catch {}
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeat = window.setInterval(() => {
      if (Date.now() - this.lastMsgAt > DEAD_AFTER_MS) {
        // Mobile networks can leave a socket half-open; force a reconnect.
        try {
          this.ws?.close(4000, "heartbeat timeout");
        } catch {}
        const ws = this.ws;
        if (ws) ws.onclose?.(new CloseEvent("close", { code: 4000 }));
        return;
      }
      this.ping();
    }, HEARTBEAT_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat !== undefined) clearInterval(this.heartbeat);
    this.heartbeat = undefined;
  }
}
